'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { Loader2, LucideIcon, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@kit/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@kit/ui/dialog';
import { useDebouncedValue } from '@kit/ui/hooks/use-debounce';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { useBrand } from '~/components/brand-provider';
import { OrganizationAvatar } from '~/components/organization-avatar';
import { superAdminOrgSearchWithLogosAction } from '~/components/organization-search.actions';
import {
  isCrossBrand,
  resolveCrossBrandHref,
} from '~/lib/routing/resolve-app-url';
import {
  adminNavKeywordsByPath,
  adminSearchGroups,
  adminSearchItems,
} from './admin-nav-data';

// Navigation item types
interface NavigationItem {
  label: string;
  path: string;
  icon: LucideIcon;
  keywords?: string[];
}

// Admin menu structure + search synonyms come from admin-tab-context.tsx (Rule #8).

// Get favorites from localStorage
function getFavorites(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem('nav-favorites');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save favorites to localStorage
function saveFavorites(favorites: string[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem('nav-favorites', JSON.stringify(favorites));
}

// Get recent items from localStorage
function getRecentItems(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem('nav-recent');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Add item to recent items
function addToRecent(path: string) {
  if (typeof window === 'undefined') return;
  const recent = getRecentItems().filter((p) => p !== path);
  recent.unshift(path);
  localStorage.setItem('nav-recent', JSON.stringify(recent.slice(0, 5)));
}

// All items flattened for searching: the sidebar items AND the pages that
// are reachable only as a module tab (admin-nav-data.ts derives both).
const allItems = adminSearchItems.map((item) => ({
  ...item,
  keywords: adminNavKeywordsByPath[item.path],
}));

/** cmdk match text: label, hosting module, group and synonyms. */
function searchValue(item: {
  label: string;
  path: string;
  group: string;
  module?: string;
}): string {
  return [
    item.label,
    item.module ?? '',
    item.group,
    adminNavKeywordsByPath[item.path]?.join(' ') ?? '',
  ].join(' ');
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const brand = useBrand();
  const [favorites, setFavorites] = useState<string[]>([]);
  const [recent, setRecent] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Load favorites and recent on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from localStorage when palette opens
    setFavorites(getFavorites());
    setRecent(getRecentItems());
  }, [open]); // Refresh when opening

  // Super-admin org search. The palette is mounted only for super admins, and
  // the RPC also hard-gates on is_super_admin(); the org list is never
  // eager-loaded (there are thousands of orgs), it queries as the admin types.
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const orgSearchEnabled = debouncedSearch.length >= 2;

  const orgSearch = useQuery({
    queryKey: ['command-palette-org-search', debouncedSearch],
    enabled: orgSearchEnabled,
    queryFn: () =>
      superAdminOrgSearchWithLogosAction({
        search: debouncedSearch,
        maxResults: 10,
      }),
  });

  const handleSelectOrg = useCallback(
    (slug: string) => {
      onOpenChange(false);
      router.push(`/home/${slug}/members`);
    },
    [router, onOpenChange],
  );

  const handleSelect = useCallback(
    (path: string) => {
      addToRecent(path);
      onOpenChange(false);
      // Admin routes are Flow-owned; selecting one from the Ballee surface is a
      // cross-brand hop. Route it through the authenticated handoff endpoint
      // (a hard navigation, since the handoff is an API route the client router
      // can't render). Same-brand/shared paths stay fast client nav.
      if (isCrossBrand(path, brand)) {
        window.location.assign(resolveCrossBrandHref(path, brand));
        return;
      }
      router.push(path);
    },
    [router, onOpenChange, brand],
  );

  const toggleFavorite = useCallback((path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites((prev) => {
      const newFavorites = prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path].slice(0, 5); // Max 5 favorites
      saveFavorites(newFavorites);
      return newFavorites;
    });
  }, []);

  // Find items by path
  const getItemByPath = (path: string) => allItems.find((i) => i.path === path);

  // Favorite items
  const favoriteItems = favorites
    .map((path) => getItemByPath(path))
    .filter(Boolean) as (NavigationItem & { group: string })[];

  // Recent items (excluding favorites)
  const recentItems = recent
    .filter((path) => !favorites.includes(path))
    .map((path) => getItemByPath(path))
    .filter(Boolean) as (NavigationItem & { group: string })[];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[500px]">
        <DialogTitle className="sr-only">
          {t('home:commandPalette.title')}
        </DialogTitle>
        <Command className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2">
          <CommandInput
            placeholder={t('home:commandPalette.searchPlaceholder')}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[400px]">
            <CommandEmpty>{t('home:commandPalette.noResults')}</CommandEmpty>

            {/* Organizations (super admin) — server-side search, never eager-loaded */}
            {orgSearchEnabled && (
              <CommandGroup heading={t('home:commandPalette.organizations')}>
                {orgSearch.isLoading && (
                  <div
                    className={
                      'text-muted-foreground flex items-center gap-x-2 px-2 py-1.5 text-xs'
                    }
                  >
                    <Loader2 className={'h-3 w-3 animate-spin'} />
                    <Trans i18nKey={'teams:searchingOrganizations'} />
                  </div>
                )}
                {!orgSearch.isLoading &&
                  (orgSearch.data?.length ?? 0) === 0 && (
                    <div
                      className={'text-muted-foreground px-2 py-1.5 text-xs'}
                    >
                      <Trans i18nKey={'teams:noOrganizationsFound'} />
                    </div>
                  )}
                {(orgSearch.data ?? []).map((org) => (
                  <CommandItem
                    key={`org-${org.id}`}
                    value={`org ${org.name} ${org.slug} ${debouncedSearch}`}
                    onSelect={() => handleSelectOrg(org.slug)}
                    className="cursor-pointer"
                  >
                    <OrganizationAvatar
                      name={org.name}
                      logoUrl={org.logoUrl}
                      className="mr-2 h-5 w-5 flex-shrink-0"
                    />
                    <span>{org.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Favorites */}
            {favoriteItems.length > 0 && (
              <CommandGroup heading={t('home:commandPalette.favorites')}>
                {favoriteItems.map((item) => (
                  <CommandItem
                    key={`fav-${item.path}`}
                    value={`${item.label} ${item.group} ${item.keywords?.join(' ') || ''}`}
                    onSelect={() => handleSelect(item.path)}
                    className="cursor-pointer"
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {item.group}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={(e) => toggleFavorite(item.path, e)}
                      className="ml-auto h-auto w-auto p-0"
                    >
                      <Star
                        className={cn(
                          'h-3.5 w-3.5',
                          favorites.includes(item.path)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground',
                        )}
                      />
                    </Button>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Recent */}
            {recentItems.length > 0 && (
              <CommandGroup heading={t('home:commandPalette.recent')}>
                {recentItems.map((item) => (
                  <CommandItem
                    key={`recent-${item.path}`}
                    value={`${item.label} ${item.group} ${item.keywords?.join(' ') || ''}`}
                    onSelect={() => handleSelect(item.path)}
                    className="cursor-pointer"
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {item.group}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={(e) => toggleFavorite(item.path, e)}
                      className="ml-auto h-auto w-auto p-0"
                    >
                      <Star
                        className={cn(
                          'h-3.5 w-3.5',
                          favorites.includes(item.path)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground hover:text-yellow-400',
                        )}
                      />
                    </Button>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* All Navigation Groups (sidebar items + module-tab pages) */}
            {adminSearchGroups.map((group) => (
              <CommandGroup key={group.label} heading={group.label}>
                {group.items.map((item) => (
                  <CommandItem
                    key={item.path}
                    value={searchValue(item)}
                    onSelect={() => handleSelect(item.path)}
                    className="cursor-pointer"
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    <span>{item.label}</span>
                    {item.module ? (
                      <span className="text-muted-foreground ml-2 text-xs">
                        {item.module}
                      </span>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={(e) => toggleFavorite(item.path, e)}
                      className="ml-auto h-auto w-auto p-0"
                    >
                      <Star
                        className={cn(
                          'h-3.5 w-3.5',
                          favorites.includes(item.path)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-muted-foreground hover:text-yellow-400',
                        )}
                      />
                    </Button>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
          <div className="border-t px-3 py-2">
            <div className="text-muted-foreground flex items-center justify-between text-xs">
              <span>{t('home:commandPalette.navigateHint')}</span>
              <div className="flex items-center gap-2">
                <CommandShortcut>
                  {t('home:commandPalette.selectShortcut')}
                </CommandShortcut>
                <CommandShortcut>
                  {t('home:commandPalette.closeShortcut')}
                </CommandShortcut>
              </div>
            </div>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

// Hook to manage command palette state with keyboard shortcut
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+Shift+K / Ctrl+Shift+K — plain ⌘K belongs to the global search
      // overlay (GlobalSearchProvider); binding both to ⌘K stacked the two
      // dialogs on top of each other.
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'k'
      ) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { open, setOpen };
}
